import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../auth/api';

const gold = '#d4af37';
const cardBg = 'rgba(15,23,42,0.7)';
const confColor = { high: '#ef4444', medium: '#f59e0b', low: '#64748b' };

const Badge = ({ label, color = '#64748b' }) => (
    <span style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: 4,
        background: color + '22', border: `1px solid ${color}55`,
        color, fontSize: 11, fontWeight: 600, marginRight: 4,
    }}>{label}</span>
);
const NotFound = () => <span style={{ color: '#64748b', fontStyle: 'italic' }}>not found</span>;
const SpecValue = ({ v }) => !v || v === 'not found' ? <NotFound /> : <span>{v}</span>;

const SOURCE_COLORS = {
    portfolio: gold,
    procurement_validated: '#22c55e',
    market_intel: '#60a5fa',
    web: '#94a3b8',
    external: '#64748b',
};
const SOURCE_LABELS = {
    portfolio: 'PORTFOLIO',
    procurement_validated: 'VERIFIED',
    market_intel: 'MARKET INTEL',
    web: 'WEB',
    external: 'EXTERNAL',
};

export default function Compare() {
    const [mode, setMode] = useState('a_vs_b');
    const [intent, setIntent] = useState('equivalents');
    const [lang, setLang] = useState('vi');
    const [nameA, setNameA] = useState('');
    const [nameB, setNameB] = useState('');
    const [nameSingle, setNameSingle] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        const id = searchParams.get('id');
        if (!id || result) return;
        setLoading(true);
        api.get(`/compare/${id}`)
            .then(({ data }) => setResult(data))
            .catch(err => setError(err?.response?.data?.detail || `Could not load comparison #${id}`))
            .finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');
        setResult(null);
        setLoading(true);
        try {
            let products;
            if (mode === 'a_vs_b') {
                if (!nameA.trim() || !nameB.trim()) {
                    setError('Enter both product names.');
                    setLoading(false);
                    return;
                }
                products = [{ name: nameA.trim() }, { name: nameB.trim() }];
            } else {
                if (!nameSingle.trim()) {
                    setError('Enter a product name.');
                    setLoading(false);
                    return;
                }
                products = [{ name: nameSingle.trim() }];
            }
            const body = { mode, products, lang };
            if (mode === 'find_similar') body.intent = intent;
            const { data } = await api.post('/compare', body);
            setResult(data);
            if (data.compare_id) setSearchParams({ id: data.compare_id }, { replace: true });
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || 'Compare failed');
        } finally {
            setLoading(false);
        }
    }

    return (
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px' }}>
            <h2 style={{ color: gold, marginBottom: 4 }}>&#9878; Product Comparison</h2>
            <p style={{ color: '#94a3b8', marginBottom: 20, fontSize: 13 }}>
                Type any product name — LESIA auto-matches from the LEXIS portfolio or searches the web.
            </p>

            <form onSubmit={handleSubmit} style={{
                background: cardBg, border: '1px solid #1e293b',
                borderRadius: 8, padding: 20, marginBottom: 24,
            }}>
                {/* Mode + Lang */}
                <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
                    <div>
                        <label style={labelStyle}>Mode</label>
                        <div style={{ display: 'flex', gap: 8 }}>
                            {[['a_vs_b', 'A vs B (head-to-head)'], ['find_similar', 'Find Similar']].map(([v, lbl]) => (
                                <button key={v} type="button" onClick={() => setMode(v)}
                                    style={modeBtn(mode === v)}>{lbl}</button>
                            ))}
                        </div>
                    </div>
                    {mode === 'find_similar' && (
                        <div>
                            <label style={labelStyle}>Intent</label>
                            <div style={{ display: 'flex', gap: 8 }}>
                                {[['equivalents', 'Find alternatives'], ['oem_origin', 'Trace OEM origin']].map(([v, lbl]) => (
                                    <button key={v} type="button" onClick={() => setIntent(v)}
                                        style={modeBtn(intent === v)}>{lbl}</button>
                                ))}
                            </div>
                        </div>
                    )}
                    <div>
                        <label style={labelStyle}>Language</label>
                        <div style={{ display: 'flex', gap: 8 }}>
                            {[['vi', 'Vietnamese'], ['en', 'English']].map(([v, lbl]) => (
                                <button key={v} type="button" onClick={() => setLang(v)}
                                    style={modeBtn(lang === v)}>{lbl}</button>
                            ))}
                        </div>
                    </div>
                </div>

                {mode === 'a_vs_b' ? (
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        <NameInput label="Product A" value={nameA} onChange={setNameA}
                            placeholder="e.g. OSCOR Blue, Rohde Schwarz PR100, Flir E8 Pro" />
                        <NameInput label="Product B" value={nameB} onChange={setNameB}
                            placeholder="e.g. Amcrest IP8M, DJI Matrice 300, AR-drone" />
                    </div>
                ) : (
                    <NameInput
                        label={intent === 'oem_origin' ? 'Product to trace OEM origin for' : 'Input product (find comparable alternatives)'}
                        value={nameSingle} onChange={setNameSingle}
                        placeholder={intent === 'oem_origin' ? 'e.g. Hiltron XSPY-6000, SZMID FPB-3800' : 'e.g. OSCOR Blue, NovoQuad ND-SV007, TK-4400'} />
                )}

                <div style={{ marginTop: 12, color: '#475569', fontSize: 12 }}>
                    {mode === 'find_similar' && intent === 'equivalents' &&
                        'Corpus-backed, function-gated: returns products solving the same problem. Web supplement fires if corpus is sparse.'}
                    {mode === 'find_similar' && intent === 'oem_origin' &&
                        'Compares physical signatures against corpus equivalents — returns OEM/rebrand indicators, never assertions.'}
                    {mode === 'a_vs_b' &&
                        'LESIA checks the LEXIS portfolio first, then searches the web for any product not found internally.'}
                </div>

                <button type="submit" disabled={loading} style={{
                    marginTop: 16, padding: '10px 24px',
                    background: loading ? '#334155' : gold,
                    color: loading ? '#94a3b8' : '#0f172a',
                    border: 'none', borderRadius: 6, cursor: loading ? 'default' : 'pointer',
                    fontWeight: 700, fontSize: 14,
                }}>
                    {loading ? 'Analysing…' : 'Run Comparison'}
                </button>
                {error && <div style={{ color: '#ef4444', marginTop: 10, fontSize: 13 }}>{error}</div>}
            </form>

            {result && result.intent === 'equivalents' && <FindEquivalentsResult result={result} />}
            {result && result.intent === 'oem_origin' && <OEMResult result={result} />}
            {result && !result.intent && <CompareResult result={result} />}
        </div>
    );
}

function NameInput({ label, value, onChange, placeholder }) {
    return (
        <div style={{ flex: '1 1 280px' }}>
            <label style={labelStyle}>{label}</label>
            <input
                type="text"
                placeholder={placeholder}
                value={value}
                onChange={e => onChange(e.target.value)}
                style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '8px 10px', background: '#1e293b',
                    border: '1px solid #334155', borderRadius: 6,
                    color: '#e2e8f0', fontSize: 13,
                }}
            />
        </div>
    );
}

// ── Find Equivalents Result ──────────────────────────────────────────

function FindEquivalentsResult({ result }) {
    const tight = result.tight_equivalents || [];
    const broader = result.broader_alternatives || [];
    const fns = result.matched_functions || [];

    return (
        <div>
            {/* Header */}
            <div style={{ marginBottom: 20 }}>
                <h3 style={{ color: gold, marginBottom: 4 }}>
                    Alternatives for: <span style={{ fontWeight: 400 }}>{result.target_name}</span>
                </h3>
                {fns.length > 0 && (
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
                        Matched functions:{' '}
                        {fns.map(f => (
                            <Badge key={f.function_id} label={f.label_vi || f.label_en} color="#60a5fa" />
                        ))}
                    </div>
                )}
                {result.web_supplement_used && (
                    <div style={{ fontSize: 12, color: '#f59e0b', marginBottom: 6 }}>
                        ⚡ Corpus was sparse — web supplement fired and results were written back.
                    </div>
                )}
                {result.target_in_lexis && (
                    <Badge label="Target: PORTFOLIO" color={gold} />
                )}
            </div>

            {/* Tight equivalents */}
            {tight.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                    <h4 style={{ color: '#22c55e', marginBottom: 10, fontSize: 14 }}>
                        ✓ Tight Equivalents — drop-in substitutes (same function, high similarity)
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px,1fr))', gap: 12 }}>
                        {tight.map((c, i) => <EquivCard key={i} c={c} />)}
                    </div>
                </div>
            )}

            {/* No-tight note */}
            {result.no_tight_note && tight.length === 0 && (
                <div style={{ background: 'rgba(100,116,139,0.1)', border: '1px solid #334155', borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#94a3b8', fontStyle: 'italic' }}>
                    {result.no_tight_note}
                </div>
            )}

            {/* Broader alternatives */}
            {broader.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                    <h4 style={{ color: '#f59e0b', marginBottom: 10, fontSize: 14 }}>
                        ~ Broader Alternatives — share function, different technology or scope
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px,1fr))', gap: 12 }}>
                        {broader.map((c, i) => <EquivCard key={i} c={c} />)}
                    </div>
                </div>
            )}

            {tight.length === 0 && broader.length === 0 && (
                <div style={{ color: '#64748b', fontStyle: 'italic', marginBottom: 16 }}>
                    No alternatives found in corpus or web. Try a more specific product name.
                </div>
            )}

            <div style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid #1e293b', borderRadius: 6, padding: '10px 14px', fontSize: 12, color: '#64748b', fontStyle: 'italic' }}>
                {result.note_no_winner}
            </div>
            <div style={{ color: '#334155', fontSize: 11, marginTop: 8 }}>
                Compare #{result.compare_id} — intent: {result.intent} — lang: {result.lang}
            </div>
        </div>
    );
}

function EquivCard({ c }) {
    const srcColor = SOURCE_COLORS[c.source_flag] || '#64748b';
    const srcLabel = SOURCE_LABELS[c.source_flag] || c.source_flag?.toUpperCase() || 'EXTERNAL';

    return (
        <div style={{ background: cardBg, border: '1px solid #1e293b', borderRadius: 8, padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 6 }}>
                <div>
                    <span style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 14 }}>{c.name}</span>
                    {c._name_only && <span style={{ color: '#64748b', fontSize: 11, marginLeft: 6, fontStyle: 'italic' }}>(name only)</span>}
                </div>
                <Badge label={srcLabel} color={srcColor} />
            </div>

            {c.why_equivalent && (
                <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 6 }}>
                    {c.why_equivalent}
                </div>
            )}

            {(c.agencies?.length > 0 || c.countries?.length > 0) && (
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>
                    {c.agencies?.length > 0 && <span>Agencies: {c.agencies.join(', ')} · </span>}
                    {c.countries?.length > 0 && <span>Countries: {c.countries.join(', ')}</span>}
                </div>
            )}

            {c.evidence_count > 0 && (
                <div style={{ fontSize: 11, color: '#475569', marginBottom: 4 }}>
                    {c.evidence_count} evidence row{c.evidence_count !== 1 ? 's' : ''}
                </div>
            )}

            {c.similarity > 0 && (
                <div style={{ fontSize: 11, color: '#475569' }}>
                    Capability similarity: {(c.similarity * 100).toFixed(0)}%
                </div>
            )}

            {c.source_urls?.filter(Boolean).length > 0 && (
                <div style={{ marginTop: 6 }}>
                    {c.source_urls.filter(Boolean).slice(0, 1).map((url, i) => (
                        <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                            style={{ color: '#60a5fa', fontSize: 11, wordBreak: 'break-all' }}>
                            {url.length > 60 ? url.slice(0, 60) + '…' : url}
                        </a>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── OEM Origin Result ────────────────────────────────────────────────

function OEMResult({ result }) {
    const indicators = result.indicators || [];

    return (
        <div>
            <div style={{ marginBottom: 20 }}>
                <h3 style={{ color: gold, marginBottom: 4 }}>
                    OEM Origin Trace: <span style={{ fontWeight: 400 }}>{result.target_name}</span>
                </h3>
                <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 8 }}>
                    Physical signature comparison against corpus equivalents. Results are supply-chain indicators, never assertions.
                </p>
            </div>

            {indicators.length === 0 && (
                <div style={{ color: '#64748b', fontStyle: 'italic', marginBottom: 16 }}>
                    {result.note || 'No candidates found to compare against.'}
                </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
                {indicators.map((ind, i) => <OEMIndicatorCard key={i} ind={ind} />)}
            </div>

            <div style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid #1e293b', borderRadius: 6, padding: '10px 14px', fontSize: 12, color: '#64748b', fontStyle: 'italic' }}>
                {result.note_no_winner}
            </div>
            <div style={{ color: '#334155', fontSize: 11, marginTop: 8 }}>
                Compare #{result.compare_id} — intent: {result.intent} — lang: {result.lang}
            </div>
        </div>
    );
}

function OEMIndicatorCard({ ind }) {
    const statusColor = ind.status === 'indicator_found' ? '#f59e0b'
        : ind.status === 'no_match' ? '#22c55e'
        : '#475569';
    const statusLabel = ind.status === 'indicator_found' ? 'INDICATOR'
        : ind.status === 'no_match' ? 'NO MATCH'
        : 'INSUFFICIENT DATA';

    return (
        <div style={{ background: cardBg, border: `1px solid ${statusColor}44`, borderRadius: 8, padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Badge label={statusLabel} color={statusColor} />
                <span style={{ color: '#94a3b8', fontSize: 13 }}>vs. {ind.candidate}</span>
                {ind.confidence && (
                    <Badge label={ind.confidence.toUpperCase()} color={confColor[ind.confidence] || '#64748b'} />
                )}
            </div>

            {ind.indicator_statement && (
                <div style={{ color: '#e2e8f0', fontSize: 13, marginBottom: 8, lineHeight: 1.5 }}>
                    {ind.indicator_statement}
                </div>
            )}

            {ind.note && (
                <div style={{ color: '#64748b', fontSize: 12, fontStyle: 'italic' }}>
                    {ind.note}
                </div>
            )}

            {ind.matching_fields?.length > 0 && (
                <div style={{ marginTop: 8 }}>
                    <div style={{ color: '#475569', fontSize: 11, marginBottom: 4 }}>Matching physical fields:</div>
                    {ind.matching_fields.map((f, i) => (
                        <div key={i} style={{ fontSize: 11, color: '#94a3b8', marginBottom: 2 }}>
                            <strong style={{ color: '#64748b' }}>{f.field}:</strong>{' '}
                            target={f.target_value} / candidate={f.candidate_value}
                        </div>
                    ))}
                    {ind.source_url && (
                        <a href={ind.source_url} target="_blank" rel="noopener noreferrer"
                            style={{ color: '#60a5fa', fontSize: 11, wordBreak: 'break-all', display: 'block', marginTop: 4 }}>
                            {ind.source_url.length > 70 ? ind.source_url.slice(0, 70) + '…' : ind.source_url}
                        </a>
                    )}
                </div>
            )}
        </div>
    );
}

// ── A-vs-B Result (unchanged) ────────────────────────────────────────

export function CompareResult({ result }) {
    const products = result.products || [];
    const dims = result.dimension_comparison || [];
    const dueDiligence = result.client_due_diligence || [];

    return (
        <div>
            <div style={{ marginBottom: 20 }}>
                <h3 style={{ color: gold, marginBottom: 4 }}>
                    {result.comparison_title_vi || result.comparison_title}
                </h3>
                {result.comparison_title !== result.comparison_title_vi && result.comparison_title && (
                    <p style={{ color: '#64748b', fontSize: 12, marginBottom: 8 }}>{result.comparison_title}</p>
                )}
                <div style={{ background: cardBg, border: '1px solid #1e293b', borderRadius: 8, padding: 16, marginBottom: 8 }}>
                    <p style={{ color: '#e2e8f0', marginBottom: 6 }}>{result.executive_summary_vi || result.executive_summary}</p>
                    {result.executive_summary_vi && result.executive_summary && result.executive_summary !== result.executive_summary_vi && (
                        <p style={{ color: '#94a3b8', fontSize: 12, fontStyle: 'italic' }}>{result.executive_summary}</p>
                    )}
                </div>
                {result.from_brief && (
                    <div style={{ fontSize: 12, color: '#475569' }}>
                        Sourced from Case #{result.case_id} brief — ranks {result.ranks_compared?.join(' vs ')}
                    </div>
                )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginBottom: 24 }}>
                {products.map((p, i) => <ProductCard key={i} p={p} />)}
            </div>

            {dims.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                    <h4 style={{ color: gold, marginBottom: 10 }}>Dimension-by-dimension</h4>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                                <tr style={{ background: '#1e293b' }}>
                                    <th style={thStyle}>Dimension</th>
                                    {products.map((p, i) => <th key={i} style={thStyle}>{p.name}</th>)}
                                    <th style={thStyle}>Note</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dims.map((d, i) => (
                                    <tr key={i} style={{ background: i % 2 === 0 ? 'rgba(15,23,42,0.4)' : 'rgba(30,41,59,0.4)', borderBottom: '1px solid #1e293b' }}>
                                        <td style={tdStyle}><strong style={{ color: '#94a3b8' }}>{d.dimension}</strong></td>
                                        {products.map((p, pi) => (
                                            <td key={pi} style={tdStyle}><SpecValue v={d.values?.[p.name]} /></td>
                                        ))}
                                        <td style={{ ...tdStyle, color: '#94a3b8', fontStyle: 'italic' }}>{d.analysis_note}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {dueDiligence.length > 0 && (
                <div style={{ background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 8, padding: 16, marginBottom: 20 }}>
                    <h4 style={{ color: '#f59e0b', marginBottom: 8 }}>Client due diligence</h4>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {dueDiligence.map((item, i) => (
                            <li key={i} style={{ color: '#e2e8f0', marginBottom: 4, fontSize: 13 }}>{item}</li>
                        ))}
                    </ul>
                </div>
            )}

            <div style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid #1e293b', borderRadius: 6, padding: '10px 14px', fontSize: 12, color: '#64748b', fontStyle: 'italic' }}>
                {result.note_no_winner}
            </div>
            <div style={{ color: '#334155', fontSize: 11, marginTop: 8 }}>
                Compare #{result.compare_id} — mode: {result.mode} — lang: {result.lang}
            </div>
        </div>
    );
}

function ProductCard({ p }) {
    const specs = p.key_specs || {};
    const indicators = p.origin_indicators || [];
    const strengths = p.strengths || [];
    const weaknesses = p.weaknesses || [];

    return (
        <div style={{ background: cardBg, border: '1px solid #1e293b', borderRadius: 8, padding: 16 }}>
            <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
                    <span style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 15 }}>{p.name}</span>
                    {p.from_portfolio && <Badge label="PORTFOLIO" color={gold} />}
                    {p.source === 'web' && <Badge label="EXTERNAL" color="#64748b" />}
                    {p.source === 'brief_solution' && <Badge label="BRIEF" color="#60a5fa" />}
                    {p.portfolio_note && <Badge label="UNVERIFIED" color="#ef4444" />}
                </div>
                {p.name_vi && p.name_vi !== p.name && (
                    <div style={{ color: '#94a3b8', fontSize: 12 }}>{p.name_vi}</div>
                )}
                <div style={{ fontSize: 12, color: '#64748b' }}>
                    {p.vendor && <span>{p.vendor}</span>}
                    {p.vendor_country && p.vendor_country !== 'not found' && <span> · {p.vendor_country}</span>}
                    {p.category && <span> · {p.category}</span>}
                </div>
            </div>

            {p.spec_guard_warning && (
                <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: 12 }}>
                    <div style={{ color: '#f87171', fontWeight: 700, marginBottom: 3 }}>&#9888; Insufficient model-specific data</div>
                    <p style={{ color: '#fca5a5', margin: 0 }}>{p.spec_guard_warning}</p>
                    {p.spec_guard_warning_vi && (
                        <p style={{ color: '#ef4444', margin: '4px 0 0', fontStyle: 'italic' }}>{p.spec_guard_warning_vi}</p>
                    )}
                </div>
            )}

            {Object.keys(specs).length > 0 && (
                <div style={{ marginBottom: 10 }}>
                    <div style={{ color: gold, fontSize: 11, fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>Specifications</div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <tbody>
                            {Object.entries(specs).map(([k, v]) => (
                                <tr key={k} style={{ borderBottom: '1px solid #1e293b' }}>
                                    <td style={{ color: '#94a3b8', padding: '3px 0', paddingRight: 8, whiteSpace: 'nowrap' }}>{k}</td>
                                    <td style={{ color: '#e2e8f0', padding: '3px 0' }}><SpecValue v={v} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {(p.compliance_notes || p.cost_range) && (
                <div style={{ marginBottom: 10, fontSize: 12 }}>
                    {p.compliance_notes && p.compliance_notes !== 'not documented' && (
                        <div style={{ color: '#94a3b8', marginBottom: 3 }}><strong style={{ color: '#64748b' }}>Compliance:</strong> {p.compliance_notes}</div>
                    )}
                    {p.cost_range && p.cost_range !== 'not found' && (
                        <div style={{ color: '#94a3b8' }}><strong style={{ color: '#64748b' }}>Cost:</strong> {p.cost_range}</div>
                    )}
                </div>
            )}

            {(p.client_fit_vi || p.client_fit) && (
                <div style={{ marginBottom: 10, fontSize: 12 }}>
                    <div style={{ color: gold, fontSize: 11, fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>Client fit</div>
                    <p style={{ color: '#e2e8f0', margin: 0 }}>{p.client_fit_vi || p.client_fit}</p>
                    {p.client_fit_vi && p.client_fit && p.client_fit !== p.client_fit_vi && (
                        <p style={{ color: '#64748b', margin: '4px 0 0', fontStyle: 'italic' }}>{p.client_fit}</p>
                    )}
                </div>
            )}

            {(strengths.length > 0 || weaknesses.length > 0) && (
                <div style={{ display: 'flex', gap: 10, marginBottom: 10, fontSize: 12 }}>
                    {strengths.length > 0 && (
                        <div style={{ flex: 1 }}>
                            <div style={{ color: '#22c55e', fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Strengths</div>
                            <ul style={{ margin: 0, paddingLeft: 14 }}>
                                {strengths.map((s, i) => <li key={i} style={{ color: '#e2e8f0', marginBottom: 2 }}>{s}</li>)}
                            </ul>
                        </div>
                    )}
                    {weaknesses.length > 0 && (
                        <div style={{ flex: 1 }}>
                            <div style={{ color: '#ef4444', fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Limitations</div>
                            <ul style={{ margin: 0, paddingLeft: 14 }}>
                                {weaknesses.map((w, i) => <li key={i} style={{ color: '#e2e8f0', marginBottom: 2 }}>{w}</li>)}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {indicators.length > 0 && (
                <div style={{ background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, padding: 10 }}>
                    <div style={{ color: '#f87171', fontSize: 11, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>
                        Supply-chain indicators (client verification required)
                    </div>
                    {indicators.map((ind, i) => (
                        <div key={i} style={{ marginBottom: 8, fontSize: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                                <span style={{ color: confColor[ind.confidence] || '#64748b', fontWeight: 600, fontSize: 11 }}>
                                    [{(ind.confidence || '').toUpperCase()}]
                                </span>
                                <span style={{ color: '#e2e8f0' }}>{ind.indicator_statement}</span>
                            </div>
                            {ind.source_url && (
                                <a href={ind.source_url} target="_blank" rel="noopener noreferrer"
                                    style={{ color: '#60a5fa', fontSize: 11, wordBreak: 'break-all' }}>
                                    {ind.source_url}
                                </a>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {p.portfolio_note && (
                <div style={{ color: '#ef4444', fontSize: 11, marginTop: 8, fontStyle: 'italic' }}>{p.portfolio_note}</div>
            )}
        </div>
    );
}

const labelStyle = { display: 'block', color: '#94a3b8', fontSize: 12, marginBottom: 6, fontWeight: 600 };
function modeBtn(active) {
    return {
        padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
        background: active ? gold + '22' : 'transparent',
        border: `1px solid ${active ? gold : '#334155'}`,
        color: active ? gold : '#64748b',
    };
}
const thStyle = { padding: '8px 12px', textAlign: 'left', color: '#94a3b8', fontSize: 12, fontWeight: 600, borderBottom: '1px solid #334155' };
const tdStyle = { padding: '8px 12px', color: '#e2e8f0', fontSize: 13, verticalAlign: 'top' };
